import { authenticator, totp } from 'otplib';
import * as QRCode from 'qrcode';
import { supabase } from './supabase';

export interface TwoFactorSetup {
  secret: string;
  qrCodeUrl: string;
  manualEntryKey: string;
}

export const twoFactorService = {
  // Generate a new 2FA secret and QR code for setup
  async generateSecret(userEmail: string): Promise<TwoFactorSetup> {
    try {
      console.log('🔍 Generating 2FA secret for:', userEmail);
      
      // Generate a random secret using otplib
      const secret = authenticator.generateSecret();
      
      // Create the otpauth URL
      const otpauthUrl = authenticator.keyuri(
        userEmail,
        'Payroll System',
        secret
      );

      if (!secret || !otpauthUrl) {
        throw new Error('Failed to generate valid secret');
      }

      console.log('🔍 Secret generated successfully');
      const qrCodeUrl = await QRCode.toDataURL(otpauthUrl);
      console.log('🔍 QR code generated successfully');

      return {
        secret: secret,
        qrCodeUrl,
        manualEntryKey: secret,
      };
    } catch (error) {
      console.error('❌ Error generating 2FA secret:', error);
      throw new Error(`Failed to generate 2FA secret: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Verify a TOTP token with multiple fallback methods
  async verifyToken(secret: string, token: string): Promise<boolean> {
    try {
      // If no secret provided, this might be a login verification - get from database
      if (!secret && arguments.length === 1) {
        // This is likely being called with just a token for login verification
        console.log('🔍 No secret provided, this might be a login verification');
        return false;
      }
      
      // Use otplib for verification
      return authenticator.verify({ token, secret });
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  },

  // Synchronous token verification (for setup)
  verifyTokenSync(secret: string, token: string): boolean {
    try {
      console.log('🔍 =================================')
      console.log('🔍 STARTING TOKEN VERIFICATION (OTPLIB)')
      console.log('🔍 =================================')
      console.log('🔍 Secret provided:', secret ? `[${secret.length} chars]` : 'null')
      console.log('🔍 Token to verify:', token)
      console.log('🔍 Token type:', typeof token)
      console.log('🔍 Token length:', token ? token.length : 0)
      
      // Ensure the secret is a string and properly formatted
      if (!secret || typeof secret !== 'string') {
        console.error('❌ Invalid secret format - not a string')
        return false
      }
      
      // Clean and validate the secret
      let cleanSecret = secret.trim()
      
      // Check if secret is a JSON object (common corruption issue)
      if (cleanSecret.startsWith('{') || cleanSecret.startsWith('[')) {
        console.error('❌ Secret appears to be JSON object, not base32 string')
        console.error('❌ Raw secret:', cleanSecret)
        return false
      }
      
      // Remove any whitespace or newlines
      cleanSecret = cleanSecret.replace(/\s+/g, '')
      
      // Ensure the token is a string and has the right length
      if (!token || typeof token !== 'string' || token.length !== 6) {
        console.error('❌ Invalid token format')
        return false
      }
      
      // Validate base32 format
      const base32Regex = /^[A-Z2-7]+=*$/
      if (!base32Regex.test(cleanSecret)) {
        console.error('❌ Secret is not valid base32 format')
        console.error('❌ Expected: Only A-Z, 2-7, and = characters')
        console.error('❌ Actual secret:', cleanSecret)
        return false
      }
      
      console.log('🔍 Using cleaned secret for verification')
       console.log('🔍 Secret length:', cleanSecret.length)
       
       // Use otplib totp for verification with window tolerance
       console.log('🔍 Using otplib totp.verify...');
       
       try {
         // Configure totp options for window tolerance
         totp.options = { window: 2 }; // Allow 2 time steps before and after (±60 seconds)
         
         console.log('🔍 Current time:', new Date().toISOString());
         console.log('🔍 Current timestamp:', Math.floor(Date.now() / 1000));
         console.log('🔍 TOTP options:', totp.options);
         
         // Try both totp and authenticator verification
         const totpResult = totp.verify({
           token,
           secret: cleanSecret
         });
         
         const authResult = authenticator.verify({
           token,
           secret: cleanSecret
         });
         
         console.log('🔍 TOTP verification result:', totpResult);
         console.log('🔍 Authenticator verification result:', authResult);
         
         const result = totpResult || authResult;
         
         if (result) {
           console.log('✅ Token verification successful!');
           return true;
         } else {
           console.log('❌ Token verification failed');
           return false;
         }
       } catch (verifyError) {
         console.error('❌ Error during verification:', verifyError);
         return false;
       }
    } catch (error) {
      console.error('❌ Error verifying token:', error);
      return false;
    }
  },

  // Enable 2FA for a user
  async enable2FA(userId: string, secret: string): Promise<boolean> {
    try {
      console.log('🔍 twoFactorService.enable2FA called with:', { userId, secret: secret ? '[REDACTED]' : 'null' });
      
      const { data, error } = await supabase
        .from('users')
        .update({
          two_factor_secret: secret,
          two_factor_enabled: true,
        })
        .eq('id', userId)
        .select();

      console.log('🔍 Supabase update response:', { data, error });

      if (error) {
        console.error('❌ Error enabling 2FA:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        
        if (error.message && error.message.includes('does not exist')) {
          console.error('2FA database columns are missing. Please run the database setup script.');
        }
        return false;
      }

      console.log('✅ 2FA enabled successfully, updated rows:', data?.length || 0);
      return true;
    } catch (error) {
      console.error('❌ Unexpected error enabling 2FA:', error);
      return false;
    }
  },

  // Disable 2FA for a user
  async disable2FA(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          two_factor_secret: null,
          two_factor_enabled: false,
        })
        .eq('id', userId);

      if (error) {
        console.error('Error disabling 2FA:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      return false;
    }
  },

  // Check if user has 2FA enabled
  async is2FAEnabled(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('two_factor_enabled')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error checking 2FA status:', error);
        return false;
      }

      return data?.two_factor_enabled || false;
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      return false;
    }
  },

  // Get user's 2FA secret (for verification)
  async getUserSecret(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('two_factor_secret')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error getting user secret:', error);
        return null;
      }

      let secret = data?.two_factor_secret;
      if (!secret) return null;

      // Clean and validate the secret from database
      if (typeof secret !== 'string') {
        console.error('❌ Secret from database is not a string:', typeof secret);
        return null;
      }

      secret = secret.trim();

      // Check if secret is corrupted (JSON object)
      if (secret.startsWith('{') || secret.startsWith('[')) {
        console.error('❌ Secret in database is corrupted (JSON object)');
        console.error('❌ User needs to re-setup 2FA');
        // Disable 2FA for this user since secret is corrupted
        await this.disable2FA(userId);
        return null;
      }

      // Remove any whitespace
      secret = secret.replace(/\s+/g, '');

      // Validate base32 format
      const base32Regex = /^[A-Z2-7]+=*$/;
      if (!base32Regex.test(secret)) {
        console.error('❌ Secret in database is not valid base32');
        console.error('❌ User needs to re-setup 2FA');
        // Disable 2FA for this user since secret is invalid
        await this.disable2FA(userId);
        return null;
      }

      return secret;
    } catch (error) {
      console.error('Error getting user secret:', error);
      return null;
    }
  },

  // Verify token for login (gets secret from database)
  async verifyTokenForUser(userId: string, token: string): Promise<boolean> {
    try {
      console.log('🔍 =================================');
      console.log('🔍 VERIFYING TOKEN FOR USER LOGIN');
      console.log('🔍 =================================');
      console.log('🔍 User ID:', userId);
      console.log('🔍 Token:', token);
      
      const secret = await this.getUserSecret(userId);
      if (!secret) {
        console.error('❌ No 2FA secret found for user');
        return false;
      }
      
      console.log('🔍 Retrieved secret from database for verification');
      console.log('🔍 Secret length:', secret.length);
      console.log('🔍 Secret first 10 chars:', secret.substring(0, 10) + '...');
      
      const result = this.verifyTokenSync(secret, token);
      console.log('🔍 Final verification result:', result);
      return result;
    } catch (error) {
      console.error('❌ Error verifying token for user:', error);
      return false;
    }
  },
};